import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, BadgePercent, ChevronDown } from "lucide-react";
import { getIcon } from "./iconMap";
import i18n from "@/i18n";

const OPEN_KEY = "airpog_strip_open";

// 🔥 THE POG STRIP — collapsible trending-category chips rail (dropdown pill, Map Cleanup).
export const PogStrip = ({ show, rail, wantedCount, onCategory, onWanted, jobsCount = 0, jobsNew = 0, onJobs, reelsCount = 0, reelsNow = 0, onReels, saleWeekend = 0, onSaleWeekend }) => {
  const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) === "1");
  const [peek, setPeek] = useState(false);
  const toggle = () => { setPeek(false); setOpen((v) => { localStorage.setItem(OPEN_KEY, v ? "0" : "1"); return !v; }); };
  const hotCount = rail.reduce((n, t) => n + (t.deals || 0), 0);
  const prevHot = useRef(0);
  // 👀 Auto-peek: hot deals just appeared while collapsed — flash the strip open for 6s
  useEffect(() => {
    if (!open && hotCount > prevHot.current && hotCount > 0) {
      setPeek(true);
      const t = setTimeout(() => setPeek(false), 6000);
      prevHot.current = hotCount;
      return () => clearTimeout(t);
    }
    prevHot.current = hotCount;
  }, [hotCount, open]);
  const expanded = open || peek;
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="trending-rail"
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
          className="min-w-[250px] flex-1 flex items-center gap-2 overflow-x-auto no-scrollbar pointer-events-auto"
        >
          <button
            data-testid="pog-strip-toggle"
            onClick={toggle}
            className="shrink-0 flex items-center gap-2 bg-slate-900/90 rounded-xl px-2.5 py-1.5 ring-1 ring-fuchsia-400/40 shadow-lg hover:bg-slate-800 transition-colors"
            title={open ? "Collapse the POG Strip" : "Open the POG Strip — trending near you"}
          >
            <span className="flex flex-col leading-none text-left">
              <span className="text-[11px] font-heading font-black uppercase tracking-wider text-fuchsia-300 flex items-center gap-1">
                <Flame className="w-3.5 h-3.5" /> The POG Strip
              </span>
              <span className="text-[8px] font-bold uppercase tracking-widest text-slate-300 pl-4">trending near you</span>
            </span>
            {!expanded && hotCount > 0 && (
              <span data-testid="pog-strip-hot-count" className="text-[10px] font-black text-amber-300 bg-amber-500/15 border border-amber-400/40 rounded-full px-1.5 py-0.5">🔥 {hotCount}</span>
            )}
            <ChevronDown className={`w-4 h-4 text-fuchsia-300 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
          {expanded && rail.map((t) => {
            const Ico = getIcon(t.icon);
            return (
              <button
                key={t.id}
                data-testid={`trending-chip-${t.id}`}
                onClick={() => onCategory(t.id)}
                className={`shrink-0 h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 bg-slate-900/85 backdrop-blur-md border border-slate-600/50 text-white shadow-lg hover:bg-slate-800 transition-colors ${
                  t.deals > 0 ? "ring-1 ring-amber-300/70 shadow-[0_0_14px_rgba(251,191,36,0.45)] animate-[stripglow_2.4s_ease-in-out_infinite]"
                  : t.spotlight ? "ring-1 ring-fuchsia-400/80 shadow-[0_0_14px_rgba(232,121,249,0.5)]" : ""
                }`}
              >
                {t.deals > 0 && <span className="-ml-0.5">🔥</span>}
                {t.spotlight && t.deals === 0 && <span className="-ml-0.5">🧩</span>}
                <Ico className="w-3.5 h-3.5 text-sky-300" /> {i18n.t(`categories.${t.id}`, { defaultValue: t.label })}
                {t.deals > 0 && (
                  <span className="ml-0.5 inline-flex items-center gap-0.5 text-emerald-300"><BadgePercent className="w-3 h-3" />{t.deals}</span>
                )}
              </button>
            );
          })}
          {expanded && wantedCount > 0 && (
            <button
              data-testid="wanted-map-chip"
              onClick={onWanted}
              className="shrink-0 h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 bg-slate-900/85 backdrop-blur-md border border-amber-400/50 text-amber-200 shadow-lg hover:bg-slate-800 transition-colors"
            >
              🤠 WANTED · {wantedCount}
            </button>
          )}
          {expanded && (
            <button
              data-testid="jobs-map-chip"
              onClick={onJobs}
              className="shrink-0 h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 bg-slate-900/85 backdrop-blur-md border border-emerald-400/50 text-emerald-200 shadow-lg hover:bg-slate-800 transition-colors"
            >
              💼 JOBS RADAR{jobsCount > 0 ? ` · ${jobsCount}` : ""}{jobsNew > 0 ? ` · +${jobsNew} 🔔` : ""}
            </button>
          )}
          {expanded && (
            <button
              data-testid="reels-map-chip"
              onClick={onReels}
              className={`shrink-0 h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 bg-slate-900/85 backdrop-blur-md border text-fuchsia-200 shadow-lg hover:bg-slate-800 transition-colors ${reelsNow > 0 ? "border-red-400/70 shadow-[0_0_14px_rgba(248,113,113,0.5)] animate-[stripglow_2.4s_ease-in-out_infinite]" : "border-fuchsia-400/50"}`}
            >
              🔥 PICS &amp; FLICS{reelsCount > 0 ? ` · ${reelsCount}` : ""}{reelsNow > 0 ? <span className="text-red-300 font-black">🔴 {reelsNow} NOW</span> : null}
            </button>
          )}
          {expanded && saleWeekend > 0 && (
            <button
              data-testid="sale-weekend-chip"
              onClick={onSaleWeekend}
              className="shrink-0 h-8 px-3 rounded-full text-xs font-black whitespace-nowrap flex items-center gap-1.5 bg-amber-400 text-slate-900 border border-amber-200 shadow-lg shadow-amber-500/40 animate-pulse hover:bg-amber-300 transition-colors"
            >
              🛍 SALE WEEKEND · {saleWeekend}
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
